import React, { useEffect, useRef, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tent, LogOut, LayoutDashboard, Clock, Users, FileText, MessageSquareWarning, CalendarDays } from "lucide-react";

const IDLE_LIMIT = 15 * 60 * 1000; // 15 min
const REFRESH_INTERVAL = 10 * 60 * 1000;

export default function AppLayout() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const idleTimer = useRef(null);

  const doLogout = useCallback(
    async (idle) => {
      await logout();
      if (idle) toast.info("You were logged out after 15 minutes of inactivity.");
      navigate("/login");
    },
    [logout, navigate]
  );

  useEffect(() => {
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => doLogout(true), IDLE_LIMIT);
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    const refreshId = setInterval(() => refresh(), REFRESH_INTERVAL);
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      clearInterval(refreshId);
    };
  }, [doLogout, refresh]);

  const isAdmin = user?.role === "admin";

  const staffNav = [
    { to: "/", label: "Clock", icon: Clock, end: true },
    { to: "/timesheets", label: "My Timesheets", icon: FileText },
    { to: "/roster", label: "My Roster", icon: CalendarDays },
  ];
  const adminNav = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/staff", label: "Staff", icon: Users },
    { to: "/admin/roster", label: "Roster", icon: CalendarDays },
    { to: "/admin/corrections", label: "Corrections", icon: MessageSquareWarning },
    { to: "/admin/reports", label: "Reports", icon: FileText },
  ];
  const nav = isAdmin ? adminNav : staffNav;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="no-print sticky top-0 z-40 bg-primary text-primary-foreground shadow-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-primary-foreground/15 flex items-center justify-center">
              <Tent className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <p className="font-heading font-bold text-base sm:text-lg">Shady Acres</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-primary-foreground/70">Timesheets</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-primary-foreground/80" data-testid="current-user-name">
              {user?.name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => doLogout(false)}
              className="text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto border-t border-primary-foreground/10">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? "border-primary-foreground text-primary-foreground"
                      : "border-transparent text-primary-foreground/70 hover:text-primary-foreground"
                  }`
                }
              >
                <Icon className="h-4 w-4" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
